import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded';
import { ChatDataTable } from './ChatDataTable';
import {
  MUTED_MARK,
  PRIMARY_MARK,
  SERIES,
  STATUS_MARK,
  axisTick,
  fitText,
  formatMeasure,
  isTickIndex,
  num,
  shortenDateLabels,
  textWidth,
  tickStep,
  toneForLabel,
  vizChrome,
} from './vizTokens';
import type { StatusTone } from './vizTokens';

/**
 * Charts for the assistant's ```chart blocks.
 *
 * **Hand-authored SVG on purpose.** This app ships no charting library and none may be
 * added, so every mark below is a `<rect>`, `<path>` or `<circle>` positioned by
 * arithmetic. That is also why there are no hover tooltips: a tooltip is the one
 * affordance a hand-rolled chart cannot make keyboard- and touch-safe cheaply. Values
 * are printed directly on the marks instead, and every chart carries a table twin —
 * which is required anyway, because the darkest and lightest palette steps sit below
 * 3:1 against the surface and are only allowed with a redundant channel.
 */

export type ChatChartType = 'bar' | 'column' | 'line' | 'area' | 'donut' | 'meter';

export interface ChatChartDatum {
  label: string;
  value?: number;
  /** Force a status colour (pass/fail style data). */
  tone?: StatusTone;
  /** Multi-series values live as extra keys named by `series`. */
  [key: string]: unknown;
}

export interface ChatChartSpec {
  type?: ChatChartType;
  title?: string;
  subtitle?: string;
  /** Value suffix, e.g. "%". */
  unit?: string;
  /** Meter only: the target the value is measured against. */
  max?: number;
  data: ChatChartDatum[];
  /** Multi-series keys. Omit for a single series. */
  series?: string[];
  /** One label takes the accent, the rest go de-emphasis grey. */
  highlight?: string;
  /** Colour marks by status meaning instead of identity. */
  status_colors?: boolean;
}

const AXIS_FONT = 10;
const LABEL_FONT = 10;

/**
 * Measure the host element so the SVG can be laid out in real pixels.
 *
 * The alternative — a fixed `viewBox` scaled to fit — makes every tick and value label
 * grow and shrink with the panel, which looks broken next to 13px body copy in a
 * resizable chat panel. `ResizeObserver` is feature-detected because jsdom has none.
 */
function useMeasuredWidth(fallback: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => {
      const next = Math.round(el.clientWidth);
      if (next > 0) setWidth(next);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width] as const;
}

/**
 * A label plus the full text behind it.
 *
 * The `<title>` is a sibling of the `<text>`, not a child: a title inside the text node
 * would be concatenated into its `textContent` and the visible string would stop being
 * addressable on its own.
 */
function AxisLabel({
  full,
  shown,
  children,
}: {
  full: string;
  shown: string;
  children: ReactNode;
}) {
  return (
    <g>
      {shown !== full && <title>{full}</title>}
      {children}
    </g>
  );
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/** One donut slice as a closed path — outer arc, inner arc back, no stroke. */
function donutSlice(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startDeg: number,
  endDeg: number,
): string {
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, rOuter, startDeg);
  const [x2, y2] = polar(cx, cy, rOuter, endDeg);
  const [x3, y3] = polar(cx, cy, rInner, endDeg);
  const [x4, y4] = polar(cx, cy, rInner, startDeg);
  const f = (n: number) => n.toFixed(2);
  return [
    `M${f(x1)},${f(y1)}`,
    `A${f(rOuter)},${f(rOuter)} 0 ${large} 1 ${f(x2)},${f(y2)}`,
    `L${f(x3)},${f(y3)}`,
    `A${f(rInner)},${f(rInner)} 0 ${large} 0 ${f(x4)},${f(y4)}`,
    'Z',
  ].join(' ');
}

export function ChatChart({ spec }: { spec: ChatChartSpec }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const chrome = vizChrome(theme);
  const [showTable, setShowTable] = useState(false);
  const [hostRef, width] = useMeasuredWidth(460);

  const data = useMemo(
    () => (spec?.data || []).filter((d) => d && typeof d === 'object' && d.label != null),
    [spec],
  );
  const seriesKeys = spec?.series?.length ? spec.series : null;
  const unit = spec?.unit || '';
  const type: ChatChartType = spec?.type || 'bar';

  /**
   * Money is decided per chart, not per mark: `unit` first, then the title, so a chart
   * called "Requested amount by store" prints dollars even when the model omits a unit.
   */
  const measure = useCallback(
    (v: number) => formatMeasure(v, unit, spec?.title),
    [unit, spec?.title],
  );

  /** Table twin: every value reachable without hovering a mark, and copyable. */
  const twin = useMemo(() => {
    const headers = ['Label', ...(seriesKeys ?? ['Value'])];
    const rows = data.map((d) => [
      String(d.label),
      ...(seriesKeys
        ? seriesKeys.map((k) => measure(num(d[k])))
        : [measure(num(d.value))]),
    ]);
    return { headers, rows };
  }, [data, seriesKeys, measure]);

  /** Colour for a single-series mark: status meaning > emphasis > one flat hue. */
  const markColor = useCallback(
    (d: ChatChartDatum): string => {
      if (spec.status_colors || d.tone) return STATUS_MARK[d.tone ?? toneForLabel(d.label)];
      if (spec.highlight) return d.label === spec.highlight ? PRIMARY_MARK : MUTED_MARK;
      return PRIMARY_MARK;
    },
    [spec.status_colors, spec.highlight],
  );

  // Every hook has now run. Bailing out above `twin`'s useMemo would make the hook
  // count depend on the data, and a spec that streams in empty and then fills would
  // crash with "rendered fewer hooks than expected".
  if (!data.length) return null;

  const keys = seriesKeys ?? ['value'];
  const maxValue = Math.max(
    1,
    ...data.flatMap((d) => keys.map((k) => num(k === 'value' ? d.value : d[k]))),
  );
  const valueOf = (d: ChatChartDatum, k: string) => num(k === 'value' ? d.value : d[k]);
  const colorFor = (d: ChatChartDatum, j: number) =>
    seriesKeys ? SERIES[j % SERIES.length] : markColor(d);

  const legendItems = seriesKeys
    ? seriesKeys.map((k, i) => ({ name: k, color: SERIES[i % SERIES.length] }))
    : spec.status_colors
      ? Array.from(new Set(data.map((d) => d.tone ?? toneForLabel(d.label)))).map((t) => ({
          name: t,
          color: STATUS_MARK[t],
        }))
      : [];

  const summary = `${spec.title || 'Chart'}: ${data
    .slice(0, 8)
    .map((d) => `${d.label} ${measure(num(d.value))}`)
    .join(', ')}`;

  /* ── Horizontal bars — the default; tolerates long category names ────────── */
  function BarChart() {
    const labelFont = AXIS_FONT + 1;
    // Clear air between a label and its bar, and never flush against the left edge.
    const GAP = 8;
    const INSET = 3;

    /**
     * The gutter is sized from the **actual longest label**, not a blanket 140px cap.
     * These labels are right-aligned, so an under-sized gutter pushes the start of the
     * string past x=0 and the SVG viewport eats it: `Withdrawn` became `/ithdrawn` and
     * `FORENSIC_PROOF_FAILED` became `RENSIC_PROOF_FAILED` — the prefix is exactly the
     * part that identifies the category. Anything still over budget is trimmed at the
     * END with an ellipsis and keeps its full text in a `<title>`.
     */
    const budget = Math.max(72, Math.min(240, Math.round(width * 0.42)));
    const widest = Math.max(...data.map((d) => textWidth(String(d.label), labelFont)));
    const textW = Math.min(budget, Math.max(48, widest));
    const labelW = Math.ceil(textW + GAP + INSET);

    // Reserve the real width of the widest value label too, now that money renders in
    // full (`$1,233,100.00`) instead of as `1.2M`.
    const valueW = Math.ceil(
      Math.max(
        44,
        Math.min(
          160,
          Math.max(
            ...data.flatMap((d) => keys.map((k) => textWidth(measure(valueOf(d, k)), LABEL_FONT))),
          ) + 14,
        ),
      ),
    );
    const rowH = seriesKeys ? 12 + keys.length * 15 : 28;
    const height = data.length * rowH + 8;
    const chartW = Math.max(width, labelW + valueW + 140);
    const plotW = Math.max(40, chartW - labelW - valueW);
    const barH = seriesKeys ? Math.min(12, (rowH - 12) / keys.length - 2) : 16;

    return (
      <svg
        role="img"
        aria-label={summary}
        width={chartW}
        height={height}
        style={{ display: 'block' }}
      >
        {data.map((d, i) => {
          const y0 = 4 + i * rowH;
          const groupH = keys.length * (barH + 2) - 2;
          const top = y0 + (rowH - groupH) / 2;
          const full = String(d.label);
          const shown = fitText(full, labelFont, textW);
          return (
            <g key={`row-${i}`}>
              <AxisLabel full={full} shown={shown}>
                <text
                  className="viz-tick"
                  x={labelW - GAP}
                  y={y0 + rowH / 2}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontSize={labelFont}
                  fill={chrome.axis}
                >
                  {shown}
                </text>
              </AxisLabel>
              {keys.map((k, j) => {
                const v = valueOf(d, k);
                const w = Math.max(2, (Math.max(0, v) / maxValue) * plotW);
                const y = top + j * (barH + 2);
                return (
                  <g key={`bar-${i}-${j}`}>
                    <rect
                      x={labelW}
                      y={y}
                      width={w}
                      height={barH}
                      rx={3}
                      fill={colorFor(d, j)}
                    />
                    <text
                      className="viz-value"
                      x={labelW + w + 6}
                      y={y + barH / 2}
                      dominantBaseline="central"
                      fontSize={LABEL_FONT}
                      fontWeight={700}
                      fill={chrome.ink}
                    >
                      {measure(v)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    );
  }

  /* ── Vertical columns — time buckets ─────────────────────────────────────── */
  function ColumnChart() {
    const topPad = 20;
    const bottomPad = 24;
    const plotH = 150;
    const height = topPad + plotH + bottomPad;
    const perBand = seriesKeys ? 24 + keys.length * 12 : 48;
    const chartW = Math.max(width, data.length * perBand + 16);
    const plotW = Math.max(40, chartW - 8);
    const band = plotW / data.length;
    const barW = Math.min(24, Math.max(6, (band * 0.62) / keys.length));
    const groupW = barW * keys.length + 2 * (keys.length - 1);

    /**
     * A 31-day series printed 31 date labels on top of each other, and scrolling did
     * not help because they overlapped at every scroll position. Shorten what does not
     * vary (one month → `7`), then draw every nth — **every bar stays**, only the
     * labels thin out.
     */
    const axisLabels = shortenDateLabels(data.map((d) => String(d.label)));
    const step = tickStep(data.length);
    const tickW = band * Math.min(step, 2) - 4;

    return (
      <svg
        role="img"
        aria-label={summary}
        width={chartW}
        height={height}
        style={{ display: 'block' }}
      >
        {[0, 0.5, 1].map((f) => (
          <line
            key={`grid-${f}`}
            x1={4}
            x2={4 + plotW}
            y1={topPad + plotH * (1 - f)}
            y2={topPad + plotH * (1 - f)}
            stroke={chrome.grid}
            strokeWidth={1}
          />
        ))}
        {data.map((d, i) => {
          const x0 = 4 + band * i + (band - groupW) / 2;
          const full = String(d.label);
          const shown = fitText(axisLabels[i] ?? full, AXIS_FONT, Math.max(28, tickW));
          return (
            <g key={`col-${i}`}>
              {keys.map((k, j) => {
                const v = valueOf(d, k);
                const h = Math.max(2, (Math.max(0, v) / maxValue) * plotH);
                const x = x0 + j * (barW + 2);
                const y = topPad + plotH - h;
                return (
                  <g key={`col-${i}-${j}`}>
                    <rect x={x} y={y} width={barW} height={h} rx={3} fill={colorFor(d, j)} />
                    <text
                      className="viz-value"
                      x={x + barW / 2}
                      y={y - 5}
                      textAnchor="middle"
                      fontSize={LABEL_FONT}
                      fontWeight={700}
                      fill={chrome.ink}
                    >
                      {measure(v)}
                    </text>
                  </g>
                );
              })}
              {isTickIndex(i, data.length, step) && (
                <AxisLabel full={full} shown={shown}>
                  <text
                    className="viz-tick"
                    x={4 + band * i + band / 2}
                    y={topPad + plotH + 15}
                    textAnchor="middle"
                    fontSize={AXIS_FONT}
                    fill={chrome.axis}
                  >
                    {shown}
                  </text>
                </AxisLabel>
              )}
            </g>
          );
        })}
      </svg>
    );
  }

  /* ── Line / area — a trend over time ─────────────────────────────────────── */
  function LineChart() {
    const leftPad = 42;
    const rightPad = 14;
    const topPad = 12;
    const bottomPad = 24;
    const plotH = 150;
    const height = topPad + plotH + bottomPad;
    const chartW = Math.max(width, data.length * 44 + leftPad + rightPad);
    const plotW = Math.max(40, chartW - leftPad - rightPad);
    const xAt = (i: number) =>
      data.length > 1 ? leftPad + (i * plotW) / (data.length - 1) : leftPad + plotW / 2;
    const yAt = (v: number) => topPad + plotH - (Math.max(0, v) / maxValue) * plotH;
    // Same contract as the columns: every point keeps its dot, the labels thin out.
    const axisLabels = shortenDateLabels(data.map((d) => String(d.label)));
    const step = tickStep(data.length, 8);
    const tickW = (plotW / Math.max(1, data.length - 1)) * step - 6;

    return (
      <svg
        role="img"
        aria-label={summary}
        width={chartW}
        height={height}
        style={{ display: 'block' }}
      >
        {[0, 0.5, 1].map((f) => (
          <g key={`grid-${f}`}>
            <line
              x1={leftPad}
              x2={leftPad + plotW}
              y1={topPad + plotH * (1 - f)}
              y2={topPad + plotH * (1 - f)}
              stroke={chrome.grid}
              strokeWidth={1}
            />
            <text
              x={leftPad - 6}
              y={topPad + plotH * (1 - f)}
              textAnchor="end"
              dominantBaseline="central"
              fontSize={AXIS_FONT}
              fill={chrome.axis}
            >
              {axisTick(maxValue * f)}
            </text>
          </g>
        ))}
        {keys.map((k, j) => {
          const color = seriesKeys ? SERIES[j % SERIES.length] : PRIMARY_MARK;
          const pts = data.map((d, i) => `${xAt(i).toFixed(2)},${yAt(valueOf(d, k)).toFixed(2)}`);
          const line = `M${pts.join(' L')}`;
          return (
            <g key={`series-${k}`}>
              {type === 'area' && (
                <path
                  d={`${line} L${xAt(data.length - 1).toFixed(2)},${(topPad + plotH).toFixed(2)} L${xAt(0).toFixed(2)},${(topPad + plotH).toFixed(2)} Z`}
                  fill={color}
                  fillOpacity={0.12}
                />
              )}
              <path
                d={line}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {data.map((d, i) => (
                <circle
                  key={`dot-${k}-${i}`}
                  cx={xAt(i)}
                  cy={yAt(valueOf(d, k))}
                  r={2.75}
                  fill={color}
                  stroke={chrome.surface}
                  strokeWidth={1.5}
                />
              ))}
            </g>
          );
        })}
        {data.map((d, i) => {
          if (!isTickIndex(i, data.length, step)) return null;
          const full = String(d.label);
          const shown = fitText(axisLabels[i] ?? full, AXIS_FONT, Math.max(30, tickW));
          return (
            <AxisLabel key={`xl-${i}`} full={full} shown={shown}>
              <text
                className="viz-tick"
                x={xAt(i)}
                y={topPad + plotH + 15}
                textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
                fontSize={AXIS_FONT}
                fill={chrome.axis}
              >
                {shown}
              </text>
            </AxisLabel>
          );
        })}
      </svg>
    );
  }

  /* ── Donut — part-to-whole at a glance, max 6 slices ─────────────────────── */
  function DonutChart() {
    const size = Math.max(160, Math.min(200, width));
    const chartW = Math.max(width, size);
    const cx = chartW / 2;
    const cy = size / 2;
    const rOuter = size * 0.42;
    const rInner = size * 0.27;
    const total = data.reduce((a, d) => a + Math.max(0, num(d.value)), 0);
    if (total <= 0) return null;

    let cursor = 0;
    return (
      <svg
        role="img"
        aria-label={summary}
        width={chartW}
        height={size}
        style={{ display: 'block' }}
      >
        {data.map((d, i) => {
          const v = Math.max(0, num(d.value));
          const sweep = Math.min(359.9, (v / total) * 360);
          const start = cursor;
          cursor += sweep;
          if (sweep <= 0) return null;
          // 1.5deg of surface between touching fills — a gap, not a border.
          const gap = sweep > 6 ? 1.5 : 0;
          return (
            <path
              key={`slice-${i}`}
              d={donutSlice(cx, cy, rOuter, rInner, start, start + sweep - gap)}
              fill={spec.status_colors || d.tone ? markColor(d) : SERIES[i % SERIES.length]}
            />
          );
        })}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={19}
          fontWeight={800}
          fill={chrome.ink}
        >
          {measure(total)}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={AXIS_FONT}
          fill={chrome.axis}
        >
          total
        </text>
      </svg>
    );
  }

  /* ── Meter — one ratio against a target ──────────────────────────────────── */
  function Meter() {
    const value = num(data[0]?.value);
    const max = num(spec.max) || 100;
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const tone: StatusTone =
      data[0]?.tone ?? (pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'danger');
    return (
      <Box sx={{ px: 0.25, py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {String(data[0]?.label ?? '')}
          </Typography>
          <Box component="span" sx={{ fontSize: '1.1rem', fontWeight: 800, color: 'text.primary' }}>
            {measure(value)}
          </Box>
        </Box>
        <Box
          role="img"
          aria-label={`${String(data[0]?.label ?? '')}: ${measure(value)} of ${measure(max)}`}
          sx={{
            mt: 0.75,
            height: 10,
            width: '100%',
            borderRadius: '999px',
            overflow: 'hidden',
            bgcolor: chrome.track,
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${pct}%`,
              borderRadius: '999px',
              bgcolor: STATUS_MARK[tone],
              transition: 'width .3s ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`${measure(value)} of ${measure(max)} target`}
        </Typography>
      </Box>
    );
  }

  // Called as plain functions, not rendered as components: they hold no state, and
  // declaring components inside a render would remount the whole SVG every keystroke.
  const body =
    type === 'meter'
      ? Meter()
      : type === 'donut'
        ? DonutChart()
        : type === 'line' || type === 'area'
          ? LineChart()
          : type === 'column'
            ? ColumnChart()
            : BarChart();

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        my: 1.25,
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px', // literal px: `sx.borderRadius` is theme-multiplied
        bgcolor: alpha(theme.palette.text.primary, isLight ? 0.018 : 0.05),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {spec.title && (
            <Typography
              component="figcaption"
              variant="caption"
              noWrap
              sx={{ display: 'block', fontWeight: 800, color: 'text.primary', fontSize: '0.75rem' }}
            >
              {spec.title}
            </Typography>
          )}
          {spec.subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {spec.subtitle}
            </Typography>
          )}
        </Box>
        <Tooltip title={showTable ? 'Show chart' : 'Show data as table'}>
          <IconButton
            size="small"
            onClick={() => setShowTable((s) => !s)}
            aria-label={showTable ? 'Show chart' : 'Show data as table'}
            aria-pressed={showTable}
            sx={{ p: 0.5, flexShrink: 0 }}
          >
            {showTable ? (
              <InsertChartOutlinedRoundedIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <TableRowsRoundedIcon sx={{ fontSize: '1rem' }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {showTable ? (
        // The twin is deliberately link-free: it is a redundant reading of the marks,
        // not a navigation surface.
        <ChatDataTable headers={twin.headers} rows={twin.rows} linkable={false} />
      ) : (
        <>
          {/* Wide charts scroll inside their own box; the panel never scrolls sideways. */}
          <Box ref={hostRef} sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
            {body}
          </Box>
          {legendItems.length >= 2 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 0.75, px: 0.25 }}>
              {legendItems.map((l) => (
                <Box
                  key={l.name}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: l.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {l.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
